import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logoWhite from '../../../shared/assets/logo/liftpeak_white.png';
import './LegalPage.css';

export function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-logo-link">
          <img src={logoWhite} className="legal-logo" alt="Liftpeak logo" />
        </Link>
      </header>

      <main className="legal-content">
        <h1 className="legal-title">Privacy Policy</h1>
        <p className="legal-updated">Last updated: 2026-06-15</p>

        <p>
          This Privacy Policy explains how <strong>LiftPeak</strong> ("LiftPeak", "we", "us")
          collects and uses personal data when you use our mobile application and our website
          (the "Service").
        </p>

        <section className="legal-section">
          <h2>1) Who we are (Data Controller)</h2>
          <p><strong>Controller:</strong> LiftPeak OU</p>
          <p><strong>Registered address:</strong> Harju maakond, Tallinn, Lasnamae linnaosa, Sepapaja tn 6, 15551</p>
          <p><strong>Privacy contact:</strong> contact@liftpeak.com</p>
          <p>If you have questions or requests about your data, contact us at <a href="mailto:contact@liftpeak.com">contact@liftpeak.com</a>.</p>
        </section>

        <section className="legal-section">
          <h2>2) What data we collect</h2>

          <h3>A) Data you provide</h3>
          <ul>
            <li><strong>Account data:</strong> email address, username, password (stored only in hashed value), and optional profile details.</li>
            <li><strong>Social content:</strong> posts, captions, comments, likes, and other interactions you create in the app.</li>
            <li><strong>Workout and training data:</strong> workouts, exercises, sets, reps, weights, notes, timestamps, and other training logs you enter.</li>
            <li><strong>Support communications:</strong> information you send to us when you contact support.</li>
          </ul>

          <h3>B) Data collected automatically</h3>
          <ul>
            <li><strong>Usage data:</strong> how you use the app (e.g., screens viewed, actions taken, session timestamps).</li>
            <li><strong>Device & app data:</strong> device model, operating system version, language, time zone, and app version.</li>
            <li><strong>Log and security data:</strong> basic logs used to maintain security and troubleshoot issues.</li>
          </ul>

          <h3>C) Media you upload</h3>
          <p>If you upload photos (e.g., profile picture or post images), we store them and associated metadata needed to display them in the app.</p>

          <h3>D) Fitness-related data note</h3>
          <p>LiftPeak is a fitness app. The workout and training data you enter may be considered <strong>health- or fitness-related</strong> in some jurisdictions. We process it to provide the Service features (workout logging, progress tracking, and social sharing controls).</p>

          <h3>E) Subscription and purchase data</h3>
          <p>If you buy a subscription, Apple, Google, and RevenueCat process purchase and subscription data needed to activate and manage your access, such as product ID, purchase token or receipt, subscription status, renewal or expiration dates, trial or offer eligibility, and related platform account identifiers. We do not receive or store your full payment card details.</p>
        </section>

        <section className="legal-section">
          <h2>3) Device permissions</h2>
          <p>Depending on how you use LiftPeak and your device settings, the app may request:</p>
          <ul>
            <li><strong>Photos/Media Library</strong> - to select and upload profile pictures or post images.</li>
            <li><strong>Camera</strong> - to take and upload photos inside the app.</li>
            <li><strong>Notifications</strong> - to send you alerts such as reminders or social activity notifications (optional; you can disable at any time).</li>
          </ul>
          <p>You can manage or revoke permissions in your device settings.</p>
        </section>

        <section className="legal-section">
          <h2>4) How we use your data (purposes)</h2>
          <p>We use personal data to:</p>
          <ul>
            <li><strong>Provide the Service</strong> (create accounts, log workouts, show your history, enable social features).</li>
            <li><strong>Operate and secure the Service</strong> (fraud prevention, abuse prevention, troubleshooting).</li>
            <li><strong>Process subscriptions</strong> (confirm purchase status, unlock paid features, handle renewals, cancellations, trials, and support requests).</li>
            <li><strong>Improve the Service</strong> (understanding feature usage and fixing issues).</li>
            <li><strong>Communicate with you</strong> (support responses and important service messages).</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5) Legal bases for processing (GDPR/EEA)</h2>
          <p>If you are in the EU/EEA, we rely on these legal bases:</p>
          <ul>
            <li><strong>Performance of a contract</strong> (Art. 6(1)(b)) - to provide the Service you request (account, workout tracking, social features).</li>
            <li><strong>Legitimate interests</strong> (Art. 6(1)(f)) - to maintain security, prevent abuse, and improve the Service (e.g., internal analytics and debugging). We balance these interests against your rights.</li>
            <li><strong>Consent</strong> (Art. 6(1)(a)) - for optional features such as push notifications (and any other processing where we ask you for consent).</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>6) Analytics</h2>
          <p>We use <strong>Mixpanel</strong> to understand how users interact with the app (for example, which screens are used most often) so we can improve the Service. Mixpanel may process usage and device data for analytics purposes.</p>
        </section>

        <section className="legal-section">
          <h2>7) Sharing your data</h2>

          <h3>A) Public vs private content</h3>
          <p>LiftPeak includes social features. What other users can see depends on your settings and what you choose to share. If you post publicly, your content may be visible to other users.</p>

          <h3>B) Service providers (processors)</h3>
          <p>We use trusted third parties to operate the Service. These providers process personal data on our behalf only as needed to deliver their services:</p>
          <ul>
            <li><strong>Cloud storage and content delivery providers</strong> (media storage) - store and deliver uploaded images and related files.</li>
            <li><strong>Mixpanel</strong> (analytics) - measures usage and app interactions.</li>
            <li><strong>Apple and Google</strong> (platform providers) - app distribution, in-app purchase billing, subscription management, and related platform services.</li>
            <li><strong>RevenueCat</strong> (subscription processor) - validates purchases and helps us manage subscription status and paid-feature access.</li>
            <li><strong>Authentication providers:</strong>
              <ul>
                <li>Apple Sign-In</li>
                <li>Google Sign-In</li>
              </ul>
            </li>
          </ul>
          <p>If you sign in with Apple or Google, we receive basic account information necessary to authenticate you (e.g., a user identifier and, depending on your settings, email). We do not receive your Apple/Google password.</p>

          <h3>C) Legal and safety</h3>
          <p>We may disclose information if required by law, to enforce our terms, or to protect users and the Service.</p>
        </section>

        <section className="legal-section">
          <h2>8) International transfers</h2>
          <p>Our service providers may process data outside your country, including outside the EU/EEA (for example, in the United States). Where required, we rely on appropriate safeguards such as <strong>Standard Contractual Clauses</strong> approved by the European Commission.</p>
          <p>You can request information about these safeguards by contacting <a href="mailto:contact@liftpeak.com">contact@liftpeak.com</a>.</p>
        </section>

        <section className="legal-section">
          <h2>9) Data retention</h2>
          <p>We retain personal data only as long as necessary for the purposes described above:</p>
          <ul>
            <li><strong>Account data & workout data:</strong> retained until you delete your account (or delete specific content), then typically removed within <strong>30 days</strong>.</li>
            <li><strong>Public social content (posts/comments):</strong> when you delete your account, we delete your content where feasible; in some cases, we may <strong>anonymize</strong> remaining public interactions (e.g., comments) to preserve conversation threads (shown as "Deleted User").</li>
            <li><strong>Backups:</strong> may persist for up to <strong>90 days</strong> before being overwritten.</li>
            <li><strong>Subscription records:</strong> retained while needed to provide paid access, handle support, maintain purchase history, and meet legal or accounting requirements.</li>
            <li><strong>Support communications:</strong> retained for up to <strong>3 years</strong> after resolution, unless a longer period is required for legal reasons.</li>
            <li><strong>Security logs:</strong> retained for up to <strong>12 months</strong>.</li>
          </ul>
          <p>If you want content deleted earlier or have special deletion requests, contact <a href="mailto:contact@liftpeak.com">contact@liftpeak.com</a>.</p>
        </section>

        <section className="legal-section">
          <h2>10) Security</h2>
          <p>We use reasonable technical and organizational measures to protect personal data (access controls, encryption in transit, and least-privilege access). No method of transmission or storage is completely secure.</p>

          <h3>Data breach notification</h3>
          <p>If a breach occurs that is likely to result in a risk to your rights and freedoms, we will notify affected users and relevant authorities where required by law.</p>
        </section>

        <section className="legal-section">
          <h2>11) Your rights</h2>
          <p>Depending on your location (and in particular under GDPR), you may have rights to:</p>
          <ul>
            <li>access your personal data,</li>
            <li>correct it,</li>
            <li>delete it,</li>
            <li>restrict or object to processing,</li>
            <li>data portability,</li>
            <li>withdraw consent (where processing is based on consent),</li>
            <li>lodge a complaint with a data protection authority.</li>
          </ul>
          <p><strong>How to exercise rights:</strong> email <a href="mailto:contact@liftpeak.com">contact@liftpeak.com</a>.</p>
          <p>We may need to verify your identity. We aim to respond within <strong>1 month</strong> (and may extend where legally permitted for complex requests).</p>
        </section>

        <section className="legal-section">
          <h2>12) Children</h2>
          <p>LiftPeak is not intended for children under <strong>18</strong>. We do not knowingly collect personal data from children. If you believe a child has provided data, contact <a href="mailto:contact@liftpeak.com">contact@liftpeak.com</a> and we will take appropriate steps.</p>
        </section>

        <section className="legal-section">
          <h2>13) Website</h2>
          <p>Our website www.liftpeak.com is a simple informational page. We do not use analytics or advertising cookies on the website at this time. If this changes, we will update this policy.</p>
        </section>

        <section className="legal-section">
          <h2>14) Changes to this Privacy Policy</h2>
          <p>We may update this policy to reflect changes in our practices or legal requirements. We will update the "Last updated" date and, where appropriate, provide notice in the app or by email.</p>
        </section>

        <section className="legal-section">
          <h2>15) Contact</h2>
          <p>For privacy questions or requests:</p>
          <p><a href="mailto:contact@liftpeak.com">contact@liftpeak.com</a></p>
        </section>
      </main>

      <footer className="legal-footer">
        <Link to="/" className="back-link">Back to Home</Link>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;


